/**
 * Fleet — the screen this whole thing exists for.
 *
 * Eight rows, always eight rows. Two of the eight have never sent a byte: dro002
 * was skipped during the rollout for a battery problem and has no serial, and
 * whatever is behind sticker 005 appears in one shell loop in the rollout
 * document and nowhere else. They are rendered as rows with their telemetry
 * cells visibly empty, marked with a grey rail, and counted in the summary
 * strip — because a fleet page that shows six healthy units is worse than
 * useless, it is reassuring about the wrong thing.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { api } from '../api/client';
import type { FleetUnit, Liveness } from '../api/types';
import { useAsync } from '../components/useAsync';
import { FaultCount, LivenessPill, Soc } from '../components/indicators';
import { DASH, since, unitKey, unitName, watts } from '../components/format';

/** Worst first when sorting by risk. Open faults outrank everything. */
const RISK: Record<Liveness, number> = {
  never_reported: 0,
  long_offline: 1,
  offline: 2,
  online: 3,
};

function riskScore(u: FleetUnit): number {
  if (u.open_faults > 0) return -1;
  return RISK[u.liveness];
}

export function Fleet() {
  const [riskFirst, setRiskFirst] = useState(false);
  // Poll rather than stream: the fleet answer is a few kilobytes and the units
  // report every ten seconds at best. A websocket would be machinery in service
  // of nothing.
  const { data, error, loading, refreshing, reload } = useAsync(() => api.fleet(), [], {
    pollMs: 30_000,
  });

  /* Counts, not a total. A summable kilogram figure belongs on the public
     counter behind its publication gates; putting one on the operations
     dashboard would invite it being quoted as the fleet figure without them.
     A per-unit CO2e column would do the same by inviting the eye to add it
     up, which is why this is one tile and not a column. */
  const co2e = useAsync(() => api.fleetCo2e(), []);

  const units = useMemo(() => {
    if (!data) return [];
    const rows = [...data.units];
    if (riskFirst) {
      rows.sort((a, b) => riskScore(a) - riskScore(b) || (a.unit_id - b.unit_id));
    }
    return rows;
  }, [data, riskFirst]);

  const summary = useMemo(() => {
    const counts: Record<Liveness, number> = {
      online: 0,
      offline: 0,
      long_offline: 0,
      never_reported: 0,
    };
    let faults = 0;
    let warnings = 0;
    for (const u of data?.units ?? []) {
      counts[u.liveness]++;
      faults += u.open_faults;
      warnings += u.open_warnings;
    }
    return { counts, faults, warnings };
  }, [data]);

  return (
    <div className="page">
      <div className="page-head">
        <h1>Fleet</h1>
        <span className="sub">
          {data ? `${data.units_total} units on the books` : loading ? 'loading…' : ''}
          {refreshing ? ' · refreshing' : ''}
        </span>
        <div className="spacer" />
        <div className="segmented">
          <button aria-pressed={!riskFirst} onClick={() => setRiskFirst(false)}>
            Fleet order
          </button>
          <button aria-pressed={riskFirst} onClick={() => setRiskFirst(true)}>
            Risk first
          </button>
        </div>
        <button onClick={reload}>Refresh</button>
      </div>

      {error && <div className="note bad">{error}</div>}

      <div className="tiles" style={{ marginBottom: 14 }}>
        <div className="tile">
          <div className="k">Units</div>
          <div className="v">{data?.units_total ?? DASH}</div>
          <div className="n">every unit on the books</div>
        </div>
        <div className="tile">
          <div className="k">Online</div>
          <div className="v" style={{ color: 'var(--ok)' }}>
            {summary.counts.online}
          </div>
          <div className="n">heard from &lt; 15 min</div>
        </div>
        <div className="tile">
          <div className="k">Offline</div>
          <div className="v" style={{ color: 'var(--quiet)' }}>
            {summary.counts.offline}
          </div>
          <div className="n">quiet, but normal</div>
        </div>
        <div className="tile">
          <div className="k">Long offline</div>
          <div className="v" style={{ color: 'var(--warn)' }}>
            {summary.counts.long_offline}
          </div>
          <div className="n">quiet &gt; 7 days</div>
        </div>
        <div className="tile">
          <div className="k">Never reported</div>
          <div className="v" style={{ color: 'var(--unknown)' }}>
            {summary.counts.never_reported}
          </div>
          <div className="n">no data, ever</div>
        </div>
        <div className={`tile${summary.faults > 0 ? ' alarm' : ''}`}>
          <div className="k">Open faults</div>
          <div className="v">{summary.faults}</div>
          <div className="n">{summary.warnings} warnings</div>
        </div>
      </div>

      <div className="panel">
        <header>
          Units
          <span className="spacer" />
          <span style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}>
            Offline is not broken — these units are legitimately dark for days.
          </span>
        </header>
        <div className="body flush" style={{ overflowX: 'auto' }}>
          <table className="data">
            <thead>
              <tr>
                <th>Unit</th>
                <th>State</th>
                <th>Mode</th>
                <th className="num">SoC</th>
                <th className="num">Output</th>
                <th className="num">Mains in</th>
                <th className="num">Solar</th>
                <th className="num">Load</th>
                <th>Location</th>
                <th>Faults</th>
                <th>Last contact</th>
                <th>Clock</th>
              </tr>
            </thead>
            <tbody>
              {units.map((u) => {
                const silent = u.liveness === 'never_reported';
                return (
                  <tr key={u.unit_id} className={silent ? 'no-data' : undefined}>
                    <td>
                      <Link to={`/units/${unitKey(u)}`} style={{ fontWeight: 600 }}>
                        {unitName(u)}
                      </Link>
                      <span className="hint" style={{ marginLeft: 6 }}>
                        {u.model_code}
                        {u.status !== 'provisioned' ? ` · ${u.status}` : ''}
                      </span>
                    </td>
                    <td>
                      <LivenessPill liveness={u.liveness} />
                    </td>
                    <td>
                      {u.working_mode_label ?? <span className="nodata">{DASH}</span>}
                    </td>
                    <td className="num">
                      <Soc value={u.soc_pct} />
                    </td>
                    <td className="num">
                      {u.ac_out_active_w === null ? (
                        <span className="nodata">{DASH}</span>
                      ) : (
                        watts(u.ac_out_active_w)
                      )}
                    </td>
                    <td className="num">
                      {u.ac_in_power_w === null ? (
                        <span className="nodata">{DASH}</span>
                      ) : (
                        watts(u.ac_in_power_w)
                      )}
                    </td>
                    <td className="num">
                      {u.pv_power_w === null ? (
                        <span className="nodata">{DASH}</span>
                      ) : (
                        watts(u.pv_power_w)
                      )}
                    </td>
                    <td className="num">
                      {u.load_pct === null ? (
                        <span className="nodata">{DASH}</span>
                      ) : (
                        `${u.load_pct.toFixed(0)}%`
                      )}
                    </td>
                    <td>
                      {u.location_name ?? (
                        <span
                          className="nodata"
                          title="Nobody has recorded where this unit is. There is no GPS in any unit — location is entered by hand or it is unknown."
                        >
                          not recorded
                        </span>
                      )}
                    </td>
                    <td>
                      <FaultCount faults={u.open_faults} warnings={u.open_warnings} />
                    </td>
                    <td>
                      {u.last_contact_at ? (
                        <span title={u.last_contact_at}>{since(u.last_contact_at)}</span>
                      ) : (
                        <span className="nodata">never</span>
                      )}
                    </td>
                    <td>
                      <ClockCell unit={u} />
                    </td>
                  </tr>
                );
              })}
              {units.length === 0 && !loading && (
                <tr>
                  <td colSpan={12} className="hint" style={{ padding: 14 }}>
                    No units. Run the migrations, including 005_seed_fleet.sql.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="panel">
        <header>Data quality</header>
        <div className="tile">
          <div className="k">CO₂e reports</div>
          <div className="v">
            {co2e.data ? `${co2e.data.units_with_report} / ${co2e.data.units_total}` : '—'}
          </div>
          <div className="n">
            {co2e.data && co2e.data.units_with_report === 0
              ? 'none stored — blocked on the EU65is idle fuel rate'
              : `${co2e.data?.live_reports ?? 0} live reports`}
          </div>
        </div>
        <div className="body flush" style={{ overflowX: 'auto' }}>
          <table className="data">
            <thead>
              <tr>
                <th>Unit</th>
                <th>Agent</th>
                <th>Wifi</th>
                <th className="num">Disk free</th>
                <th className="num">Rows pending</th>
                <th>Backfill</th>
                <th>Last sample</th>
              </tr>
            </thead>
            <tbody>
              {units.map((u) => (
                <tr key={u.unit_id} className={u.liveness === 'never_reported' ? 'no-data' : undefined}>
                  <td>
                    <Link to={`/units/${unitKey(u)}`}>{unitName(u)}</Link>
                  </td>
                  <td>{u.agent_version ?? <span className="nodata">{DASH}</span>}</td>
                  <td>
                    {u.wifi_ssid ? (
                      // Every stored profile on these units is autoconnect=yes,
                      // including cafés and at least one stranger's network.
                      // Which SSID a unit is actually on is an operational fact.
                      <span title="Every stored wifi profile on these units autoconnects, including networks nobody chose.">
                        {u.wifi_ssid}
                      </span>
                    ) : (
                      <span className="nodata">{DASH}</span>
                    )}
                  </td>
                  <td className="num">
                    {u.disk_free_mb === null ? (
                      <span className="nodata">{DASH}</span>
                    ) : (
                      `${u.disk_free_mb} MB`
                    )}
                  </td>
                  <td className="num">
                    {u.rows_pending === null ? (
                      <span className="nodata">{DASH}</span>
                    ) : (
                      u.rows_pending.toLocaleString()
                    )}
                  </td>
                  <td>
                    {u.backfill_complete === null ? (
                      <span className="nodata">{DASH}</span>
                    ) : u.backfill_complete ? (
                      <span className="hint">complete</span>
                    ) : (
                      <span className="pill warn">in progress</span>
                    )}
                  </td>
                  <td>
                    {u.last_sample_ts ? (
                      <span title={u.last_sample_ts}>{since(u.last_sample_ts)}</span>
                    ) : (
                      <span className="nodata">none</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/**
 * Clock health.
 *
 * The Pis have no RTC. fake-hwclock restores a stale time at boot and NTP jumps
 * the clock forward about 36 seconds later; a unit with no network never syncs
 * at all and writes on the stale clock indefinitely. So a large skew or an
 * unsynced clock is not a curiosity — it is the reason a chunk of that unit's
 * timestamps will be flagged suspect.
 */
function ClockCell({ unit }: { unit: FleetUnit }) {
  if (unit.ntp_synced === null) return <span className="nodata">{DASH}</span>;
  if (!unit.ntp_synced) {
    return (
      <span className="pill warn" title="NTP has not synced. Timestamps from this unit are suspect.">
        no NTP
      </span>
    );
  }
  const skew = unit.clock_skew_s ?? 0;
  if (Math.abs(skew) > 5) {
    return (
      <span className="pill warn" title={`Unit clock is ${skew.toFixed(1)} s from ours.`}>
        {skew > 0 ? '+' : ''}
        {skew.toFixed(0)}s
      </span>
    );
  }
  return <span className="hint">synced</span>;
}
